import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getVerifications, approveVerification, rejectVerification, deleteUsers } from '../api/admin';
import ConfirmAccountDeletionModal from '../components/ConfirmAccountDeletionModal';
import Badge from '../components/Badge';
import Pagination from '../components/Pagination';
import styles from './Verifications.module.css';

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function isImageUrl(url) {
  if (!url) return false;
  // Strip query params (Supabase signed URLs have ?token=...) before checking ext
  const clean = url.split('?')[0];
  return /\.(jpg|jpeg|png|webp|gif)$/i.test(clean);
}

function isPdfUrl(url) {
  if (!url) return false;
  const clean = url.split('?')[0];
  return /\.pdf($|\?)/i.test(clean);
}

// Document types allowed for each role (others are filtered out in the detail panel)
const VERIFICATION_ALLOWED = {
  private_person: new Set(['face_scan', 'id_document']),
  vehicle_owner:  new Set(['face_scan', 'id_document', 'driver_license', 'vehicle_registration', 'insurance_proof', 'registration_sticker', 'expiration_sticker']),
  default:        new Set(['face_scan', 'id_document', 'driver_license', 'vehicle_registration', 'insurance_proof']),
};

function filterDocsForVerification(docs = [], userRole) {
  const allowed = VERIFICATION_ALLOWED[userRole] || VERIFICATION_ALLOWED.default;
  const seen = new Set();
  const out = [];
  // Prefer rows with an id (from user_documents DB table) over storage-fallback rows
  const sorted = [...docs].sort((a, b) => (a.id ? 1 : 0) - (b.id ? 1 : 0) ? -1 : 1);
  for (const doc of sorted) {
    const type = doc.document_type;
    if (!type) { out.push(doc); continue; }
    if (!allowed.has(type)) continue;
    if (seen.has(type)) continue;
    seen.add(type);
    out.push(doc);
  }
  return out;
}

function Lightbox({ url, onClose }) {
  return (
    <div className={styles.lightbox} onClick={onClose}>
      <button className={styles.lightboxClose} onClick={onClose}>✕</button>
      {isPdfUrl(url) ? (
        <iframe
          src={url}
          title="PDF Document"
          style={{ width: '85vw', height: '90vh', border: 'none', borderRadius: '8px', background: '#fff' }}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <img src={url} alt="Document" className={styles.lightboxImg} onClick={(e) => e.stopPropagation()} />
      )}
    </div>
  );
}

function DocCard({ doc }) {
  const [lightbox, setLightbox] = useState(false);
  const isImage = isImageUrl(doc.url);
  const isPdf = !isImage && isPdfUrl(doc.url);
  const label = doc.label || doc.name || 'Document';

  const status = doc.verification_status || 'pending';
  const statusColors = {
    verified: { color: '#16a34a', bg: '#f0fdf4' },
    rejected: { color: '#dc2626', bg: '#fef2f2' },
    expired:  { color: '#6b7280', bg: '#f3f4f6' },
    pending:  { color: '#d97706', bg: '#fffbeb' },
  };
  const st = statusColors[status] || statusColors.pending;

  // Expiry color coding
  let expiryDisplay = null;
  if (doc.expiry_date) {
    const today = new Date();
    const exp = new Date(doc.expiry_date);
    const threeMonths = new Date();
    threeMonths.setMonth(today.getMonth() + 3);
    const color = exp < today ? '#dc2626' : exp <= threeMonths ? '#d97706' : '#16a34a';
    const icon = exp < today ? '🔴' : exp <= threeMonths ? '⚠️' : '✅';
    expiryDisplay = (
      <span style={{ fontSize: '11px', color, fontWeight: 600, marginTop: '4px', display: 'block', padding: '0 10px 8px' }}>
        {icon} Exp: {fmt(doc.expiry_date)}
      </span>
    );
  }

  return (
    <div className={styles.docCard}>
      <div className={styles.docCardLabel}>{label}</div>
      {isImage ? (
        <div className={styles.docCardImgWrap} onClick={() => setLightbox(true)}>
          <img src={doc.url} alt={label} className={styles.docCardImg} />
          <div className={styles.docCardImgOverlay}>🔍 Click to zoom</div>
        </div>
      ) : isPdf ? (
        <a href={doc.url} target="_blank" rel="noreferrer" className={styles.docCardFile} onClick={(e) => { e.preventDefault(); setLightbox(true); }}>
          <span style={{ fontSize: 28 }}>📄</span>
          <div>
            <div className={styles.docCardFileName}>PDF Document</div>
            <div className={styles.docCardFileMeta}>Click to open ↗</div>
          </div>
        </a>
      ) : doc.url ? (
        <a href={doc.url} target="_blank" rel="noreferrer" className={styles.docCardFile}>
          <span style={{ fontSize: 28 }}>📎</span>
          <div>
            <div className={styles.docCardFileName}>Document</div>
            <div className={styles.docCardFileMeta}>Click to open ↗</div>
          </div>
        </a>
      ) : (
        <div className={styles.docCardMissing}>
          <span className={styles.docCardMissingIcon}>📎</span>
          <span style={{ fontSize: 11, color: '#9ca3af' }}>{label}</span>
          <span className={styles.docCardMissingNote}>Not uploaded</span>
        </div>
      )}
      {doc.verification_status && (
        <div style={{ fontSize: '11px', fontWeight: 600, color: st.color, background: st.bg, padding: '4px 10px', borderRadius: '6px', margin: '0 10px 8px', display: 'inline-block' }}>
          {status.replace('_', ' ').toUpperCase()}
          {doc.rejected_reason ? `: ${doc.rejected_reason}` : ''}
        </div>
      )}
      {expiryDisplay}
      {lightbox && <Lightbox url={doc.url} onClose={() => setLightbox(false)} />}
    </div>
  );
}

function DetailPanel({ user, onClose, onApprove, onReject, approving }) {
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  if (!user) return (
    <div className={styles.detailEmpty}>
      <span style={{ fontSize: 40 }}>🪪</span>
      <p>Select a user to view their documents</p>
    </div>
  );

  return (
    <div className={styles.detailPanel}>
      <div className={styles.detailHeader}>
        <h2 className={styles.detailTitle}>Identity Verification</h2>
        <button className={styles.detailClose} onClick={onClose}>✕</button>
      </div>
      <div className={styles.detailBody}>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Applicant</h3>
          <InfoRow label="Name" value={user.full_name} />
          <InfoRow label="Email" value={user.email} />
          <InfoRow label="Phone" value={user.phone} />
          <InfoRow label="Joined" value={fmt(user.created_at)} />
          <InfoRow label="Status" value={
            user.identity_verified
              ? <Badge value="true" label="Verified" />
              : user.is_rejected
              ? <Badge value="rejected" label="Rejected" />
              : <Badge value="false" label="Pending" />
          } />
          {user.rejection_reason && (
            <InfoRow label="Rejection Reason" value={user.rejection_reason} />
          )}
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Uploaded Documents</h3>
          {(() => {
            const filtered = filterDocsForVerification(user.documents, user.user_role);
            if (!filtered || filtered.length === 0) {
              return <p className={styles.noDocs}>No documents uploaded yet.</p>;
            }
            return (
              <div className={styles.docGrid}>
                {filtered.map((doc, i) => <DocCard key={i} doc={doc} />)}
              </div>
            );
          })()}
        </section>

        {!user.identity_verified && !showRejectForm && (
          <div className={styles.actions}>
            <button className={styles.approveBtn} onClick={() => onApprove(user)} disabled={approving}>
              ✓ Approve Verification
            </button>
            <button className={styles.rejectBtn} onClick={() => setShowRejectForm(true)}>
              ✕ Reject
            </button>
          </div>
        )}

        {showRejectForm && (
          <div className={styles.rejectForm}>
            <p className={styles.rejectLabel}>Rejection reason (required)</p>
            <textarea className={styles.rejectTextarea} rows={3} value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)} placeholder="Enter reason…" autoFocus />
            <div className={styles.rejectActions}>
              <button className={styles.cancelBtn} onClick={() => { setShowRejectForm(false); setRejectReason(''); }}>Cancel</button>
              <button className={styles.rejectBtn} disabled={!rejectReason.trim()}
                onClick={() => { onReject(user, rejectReason); setShowRejectForm(false); setRejectReason(''); }}>
                Confirm Reject
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className={styles.infoRow}>
      <span className={styles.infoLabel}>{label}</span>
      <span className={styles.infoValue}>{value || '—'}</span>
    </div>
  );
}

export default function Verifications() {
  const queryClient = useQueryClient();
  const { status: urlStatus } = useParams();

  // Map URL param to API param
  const statusFilter = urlStatus === 'verified' ? 'verified'
    : urlStatus === 'rejected' ? 'rejected'
    : urlStatus === 'all' ? 'all'
    : 'pending';

  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteAccounts, setDeleteAccounts] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const seenRowsRef = useRef(new Map());

  useEffect(() => {
    setPage(1);
    setSelected(null);
    setSelectedIds(new Set());
  }, [urlStatus]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['verifications', statusFilter, page],
    queryFn: () => getVerifications({ ...(statusFilter && { status: statusFilter }), page, limit: 20 }).then((r) => r.data),
    keepPreviousData: true,
  });

  const approveMutation = useMutation({
    mutationFn: (id) => approveVerification(id),
    onSuccess: () => {
      toast.success('Identity verified');
      queryClient.invalidateQueries({ queryKey: ['verifications'] });
      setSelected(null);
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }) => rejectVerification(id, reason),
    onSuccess: () => {
      toast.success('Verification rejected');
      queryClient.invalidateQueries({ queryKey: ['verifications'] });
      setSelected(null);
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed'),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: ({ ids, confirm }) => deleteUsers(ids, confirm),
    onSuccess: (res) => {
      toast.success(res.data?.message || 'Account deleted');
      setSelectedIds(new Set());
      setSelected(null);
      setDeleteAccounts(null);
      queryClient.invalidateQueries({ queryKey: ['verifications'] });
    },
    onError: (err) => setDeleteError(err.response?.data?.error || 'The account could not be deleted.'),
  });

  useEffect(() => {
    (data?.verifications || []).forEach((r) => seenRowsRef.current.set(r.id, r));
  }, [data]);

  function openDeleteDialog() {
    const accounts = [...selectedIds].map((id) => seenRowsRef.current.get(id)).filter(Boolean);
    if (accounts.length !== selectedIds.size) {
      toast.error('Some selected accounts are no longer in the list. Please reselect them.');
      return;
    }
    setDeleteError('');
    setDeleteAccounts(accounts);
  }

  function toggleSelect(id) {
    setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  function toggleAll() {
    const rows = data?.verifications || [];
    setSelectedIds(selectedIds.size === rows.length ? new Set() : new Set(rows.map((r) => r.id)));
  }

  const rows = data?.verifications || [];
  const allSelected = rows.length > 0 && selectedIds.size === rows.length;

  return (
    <>
    <div className={styles.pageLayout}>
      <div className={`${styles.tablePane} ${selected ? styles.tablePaneNarrow : ''}`}>
        <div className={styles.header}>
          <h1 className={styles.pageTitle}>
            Identity Verifications
            {urlStatus && urlStatus !== 'all' && (
              <span className={styles.filterBadge}>
                {urlStatus.charAt(0).toUpperCase() + urlStatus.slice(1)}
              </span>
            )}
          </h1>
          <div className={styles.headerRight}>
            {selectedIds.size > 0 && (
              <button className={styles.bulkDeleteBtn} onClick={openDeleteDialog} disabled={bulkDeleteMutation.isPending}>
                🗑 Delete {selectedIds.size}
              </button>
            )}
          </div>
        </div>

        {isLoading && <p className={styles.msg}>Loading…</p>}
        {isError && <p className={styles.msgError}>Failed to load.</p>}

        {data && (
          <>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Joined</th>
                    <th>Document Expiration</th>
                    <th>Docs</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={8} className={styles.empty}>No {urlStatus === 'all' ? 'verifications' : `${urlStatus || 'pending'} verifications`}.</td></tr>
                  )}
                  {rows.map((row) => (
                    <tr key={row.id}
                      className={selected?.id === row.id ? styles.rowSelected : ''}
                      onClick={() => setSelected(row)} style={{ cursor: 'pointer' }}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedIds.has(row.id)} onChange={() => toggleSelect(row.id)} />
                      </td>
                      <td className={styles.nameCell}>{row.full_name || '—'}</td>
                      <td className={styles.emailCell}>{row.email}</td>
                      <td className={styles.dateCell}>{fmt(row.created_at)}</td>
                      <td>
                        {row.earliest_expiry ? (() => {
                          const today = new Date();
                          const exp = new Date(row.earliest_expiry);
                          const threeMonths = new Date(); threeMonths.setMonth(today.getMonth() + 3);
                          const color = exp < today ? '#dc2626' : exp <= threeMonths ? '#d97706' : '#16a34a';
                          return <span style={{ color }}>{fmt(row.earliest_expiry)}</span>;
                        })() : row.has_expiring_soon ? (
                          <span style={{ color: '#d97706' }}>⚠️ Expiring soon</span>
                        ) : '—'}
                      </td>
                      <td>
                        <span className={row.documents?.length > 0 ? styles.docChip : styles.docChipMissing}>
                          {row.documents?.length > 0 ? `✓ ${row.documents.length} doc(s)` : '✗ None'}
                        </span>
                      </td>
                      <td>
                        {row.identity_verified
                          ? <Badge value="true" label="Verified" />
                          : row.is_rejected
                          ? <Badge value="rejected" label="Rejected" />
                          : <Badge value="false" label="Pending" />}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {!row.identity_verified && (
                          <div className={styles.actions}>
                            <button className={styles.approveBtn} onClick={() => approveMutation.mutate(row.id)} disabled={approveMutation.isPending}>✓</button>
                            <button className={styles.rejectBtn} onClick={() => {
                              const r = window.prompt('Rejection reason:');
                              if (r) rejectMutation.mutate({ id: row.id, reason: r });
                            }}>✕</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={data.pagination?.page || 1} pages={data.pagination?.pages || 1} onPage={setPage} />
          </>
        )}
      </div>

      <DetailPanel
        user={selected}
        onClose={() => setSelected(null)}
        onApprove={(u) => approveMutation.mutate(u.id)}
        onReject={(u, reason) => rejectMutation.mutate({ id: u.id, reason })}
        approving={approveMutation.isPending}
      />
    </div>

    {deleteAccounts && (
      <ConfirmAccountDeletionModal
        accounts={deleteAccounts}
        pending={bulkDeleteMutation.isPending}
        serverError={deleteError}
        onCancel={() => setDeleteAccounts(null)}
        onConfirm={(confirm) => {
          setDeleteError('');
          bulkDeleteMutation.mutate({ ids: deleteAccounts.map((r) => r.id), confirm });
        }}
      />
    )}
    </>
  );
}

